import { HighlightOff } from "@mui/icons-material"
import React, { FC } from "react"

import {
  AdvancedForm,
  AdvancedFormData,
  AdvancedFormFieldProps,
  AdvancedForms,
} from "src/components/common/advancedForm"
import { Button } from "src/components/common/button"
import { Drawer } from "src/components/common/drawer"
import { IconButton } from "src/components/common/iconButton"
import { BasicLayoutStyleProps } from "src/components/layouts/basic"

import { create } from "src/helpers/bem"
import { postAccount } from "src/queriesXRM/account"

import { useAccount } from "src/states/account"
import {
  mergeAccountForm,
  resetAccountForm,
  useAccountForm,
} from "src/states/accountForm"
import { Dialogs, mergeDialog, useDialog } from "src/states/dialogs"
import { TranslationMessages } from "src/translations"

import { xRMApiAccount, xRMWebshopContact } from "src/types/xRM"

import styles from "./AddEditContactDialog.module.scss"

const bem = create(styles, "AddEditContactDialog")

export type AddEditContactDialogProps = {
  messages: TranslationMessages
  style: BasicLayoutStyleProps
}

export const AddEditContactDialog: FC<AddEditContactDialogProps> = ({
  messages,
  style,
}) => {
  const profileMessages = messages.pages.account.overview.profile
  const account = useAccount()
  const form = useAccountForm()
  const [loading, setLoading] = React.useState<boolean>(false)

  const formIndex = form.findIndex((form) => form.id === "webshopContacts")
  const filteredForm = form
    .filter((form) => form.id === "webshopContacts")
    .map((form) => {
      const fields = form.fields.map((field) => {
        return { ...field }
      })
      return { ...form, fields }
    })
  const formData: AdvancedFormData = [...filteredForm] as AdvancedForms[]
  const { open, unsaved } = useDialog(
    "addEditContactDialog",
  ) as Dialogs["addEditContactDialog"]

  const closeDialog = () => {
    mergeDialog("addEditContactDialog", { open: false })
  }

  const mergeFormToAccount = async () => {
    const firstName = formData[0].fields
      .find((field) => field.name === "firstName")
      ?.value?.toString()
    const lastName = formData[0].fields
      .find((field) => field.name === "lastName")
      ?.value?.toString()
    const phone = formData[0].fields
      .find((field) => field.name === "phone")
      ?.value?.toString()
    const salutation = formData[0].fields
      .find((field) => field.name === "salutation")
      ?.value?.toString()
    const company = formData[0].fields
      .find((field) => field.name === "company")
      ?.value?.toString()
    const email = formData[0].fields
      .find((field) => field.name === "email")
      ?.value?.toString()
    const country = formData[0].fields
      .find((field) => field.name === "country")
      ?.value?.toString()
    const language = formData[0].fields
      .find((field) => field.name === "language")
      ?.value?.toString()

    const update = {
      ...account,
      webshopContacts: [
        ...(account.webshopContacts ?? []),
        {
          salutation: salutation,
          firstName: firstName,
          lastName: lastName,
          phone: phone,
          company: company,
          email: email,
          countryCodeId: country,
          contactId: account.id,
          language: language,
        } as xRMWebshopContact,
      ],
    }
    await postAccount(update as xRMApiAccount)
    mergeDialog("addEditContactDialog", { unsaved: false })
  }

  const handleMerge = (
    index: number,
    items: AdvancedFormFieldProps,
    name: string | null,
  ) => {
    if (!unsaved) mergeDialog("addEditContactDialog", { unsaved: true })
    mergeAccountForm(formIndex, items, name)
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      await mergeFormToAccount()
      resetAccountForm()
      setLoading(false)
      mergeDialog("addEditContactDialog", { open: false })
    } finally {
      setLoading(false)
    }
  }

  return (
    // The z-index values must be:
    // ShopDetailProductAttributesDialog < ManageContactsDialog < AddEditContactDialog
    <Drawer
      anchor="right"
      open={open}
      style={style}
      sx={{ zIndex: 1203 }}
      title={profileMessages.contacts.form.title}
      onClose={() => closeDialog()}
      onOpen={() => mergeDialog("addEditContactDialog", { open: true })}
    >
      <div className={bem()}>
        <div className={bem("header")}>
          <h2>{profileMessages.contacts.form.title}</h2>
          <div className={bem("close")}>
            <IconButton
              color={"primary"}
              icon={HighlightOff}
              onClick={() => {
                closeDialog()
              }}
            />
          </div>
        </div>
        <div className={bem("form")}>
          <AdvancedForm
            data={formData}
            formMessages={profileMessages.contacts.form}
            handleMergeField={handleMerge}
            messages={messages}
          ></AdvancedForm>
        </div>
        <div className={bem("footer")}>
          {unsaved && (
            <h3 className={bem("footer__info")}>
              {profileMessages.contacts.form.unsavedChanges}
            </h3>
          )}
          <Button
            buttonType="primary"
            loading={loading}
            title={profileMessages.form.saveButton}
            onClick={handleSave}
          />
        </div>
      </div>
    </Drawer>
  )
}
